/**
 * /onboarding — first-run interstitial for users with zero orgs.
 *
 * Reached three ways:
 *   1. Auto-forward from /auth/verify-email when needsOnboarding=true
 *   2. Soft-redirect from App.tsx when a needsOnboarding user lands
 *      anywhere except /onboarding or /auth/*
 *   3. Direct URL nav (no harm — already-onboarded users hitting
 *      /onboarding get redirected back to their workspace)
 *
 * Two paths from here (invite-wins precedence):
 *   - Has pending invitations → accept one to join an existing org
 *     (the common, supported self-serve path).
 *   - No invitations → call `POST /api/onboarding/resolve` (#1434), which
 *     provisions the user's personal org and default workspace. We refresh
 *     identity so the new org clears `needsOnboarding` and the bounce-effect
 *     lands them in their workspace. Idempotent, so the retry button is safe.
 *
 * Returning users (already have an org) are bounced back to their
 * default workspace via /api/workspaces/me — keeps the URL clean if
 * someone bookmarks /onboarding by mistake.
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui'
import { AuthLayout } from '@/auth/AuthLayout'
import { authClient } from '@/auth/authClient'
import { useIdentity } from '@/hooks/useIdentity'
import {
  onboardingAPI,
  invitationsAPI,
  workspacesAPI,
  recoveryAPI,
  RecoverError,
  type InvitationPreview,
} from '@/lib/api'
import { toUserMessage } from '@/lib/userMessage'
import { BillingGate } from './BillingGate'

export function Onboarding() {
  const { state, refreshIdentity } = useIdentity()
  const navigate = useNavigate()

  const [error, setError] = useState<string | null>(null)
  // Pending invitations for this user's email. `null` = still
  // loading; `[]` = none, auto-provision the personal org.
  const [invitations, setInvitations] = useState<InvitationPreview[] | null>(null)
  const [acceptingId, setAcceptingId] = useState<string | null>(null)
  // Self-serve provisioning (#1455). `provisioning` gates the spinner;
  // `provisionError` surfaces a retry. `attemptedRef` makes the
  // auto-kickoff single-shot regardless of effect re-runs.
  const [provisioning, setProvisioning] = useState(false)
  const [provisionError, setProvisionError] = useState<string | null>(null)
  const attemptedRef = useRef(false)
  // Restore prompt (#2510): when the personal org is soft-deleted and still
  // within its window, resolve returns its id + purge time. We show a
  // Restore / Log out choice instead of the old looping "Try again".
  // Recovery is never automatic — logging in only offers it.
  const [recover, setRecover] = useState<{
    orgId: string
    recoverableUntil: number
    orgName: string
  } | null>(null)
  const [restoring, setRestoring] = useState(false)
  // Set when a Restore attempt found the org already gone (404 purged / 410
  // expired). We keep the panel and its Log out visible, and swap Restore for
  // a "start fresh" action, so the user always has an explicit exit (#2510).
  const [restoreGone, setRestoreGone] = useState(false)

  // Narrow the identity state to the two scalars effects below
  // depend on. Depending on the whole `state` object re-runs the
  // effect on every render whenever IdentityProvider returns a
  // fresh object reference (notably in tests that mock
  // useIdentity), looping until OOM if the effect calls setState.
  const isReady = state.status === 'ready'
  const needsOnboarding =
    state.status === 'ready' ? state.identity.needsOnboarding : false
  // A customer with an organization but no working plan is held on this
  // screen on purpose: App.tsx's IdentityGate keeps them on /onboarding using
  // this same `billing !== 'fine'` check, and the gate render two hooks below
  // shows them the plan screen. The bounce effect must agree, or the two
  // fight — the gate renders, the bounce sends them to their workspace, the
  // IdentityGate sends them back, and the screen loops (#2484).
  const heldForBilling =
    state.status === 'ready' && (state.identity.billing ?? 'fine') !== 'fine'

  // Bounce already-onboarded users back to their workspace. Don't
  // silently render the interstitial they don't need. Not while held for
  // billing — that person belongs on the plan screen, not their workspace.
  useEffect(() => {
    if (isReady && !needsOnboarding && !heldForBilling) {
      void (async () => {
        try {
          const ws = await workspacesAPI.getMe()
          navigate(`/workspaces/${ws._id}/results`, { replace: true })
        } catch {
          // No accessible workspace yet — leave them here. Should be
          // rare since needsOnboarding=false implies orgs.length>0.
        }
      })()
    }
  }, [isReady, needsOnboarding, heldForBilling, navigate])

  // Discover any pending invitations addressed to this user. Runs
  // once when the identity becomes ready and only for users who
  // need onboarding (those with orgs already are bouncing out
  // above; querying for them is wasted work).
  useEffect(() => {
    if (!isReady || !needsOnboarding) return
    let cancelled = false
    void (async () => {
      try {
        const { invitations } = await invitationsAPI.listMine()
        if (!cancelled) setInvitations(invitations)
      } catch {
        // Discovery failure is non-fatal — fall through to the
        // pending-provisioning interstitial; the user can still
        // contact support.
        if (!cancelled) setInvitations([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isReady, needsOnboarding])

  // Finish onboarding once we've confirmed the user has no pending
  // invitations: provision the personal org and drop them into their
  // workspace. The server returns an `action` we switch on. Shared by the
  // auto-kickoff effect and the retry button; idempotent server-side, so a
  // retry after a partial failure is safe.
  const runResolve = useCallback(async () => {
    setProvisioning(true)
    setProvisionError(null)
    try {
      const result = await onboardingAPI.resolve()
      switch (result.action) {
        case 'recover':
          // The personal org exists only as a soft-deleted (recoverable)
          // row, so the server didn't mint a duplicate. Offer a one-click
          // Restore (and Log out) instead of the old looping "Try again".
          setRecover({
            orgId: result.orgId,
            recoverableUntil: result.recoverableUntil,
            orgName: result.orgName,
          })
          return
        case 'invite': {
          // A pending invitation appeared between discovery and resolve.
          // Re-fetch so the invite-accept UI renders instead of spinning.
          try {
            const { invitations } = await invitationsAPI.listMine()
            setInvitations(invitations)
          } catch {
            setProvisionError(
              'You have a pending invitation waiting. Reload the page to accept it.',
            )
          }
          return
        }
        case 'workspace':
        default:
          // The org is ready. Refresh identity so needsOnboarding flips false
          // and the bounce effect lands the user in their workspace.
          await refreshIdentity()
          return
      }
    } catch (err) {
      setProvisionError(
        toUserMessage(
          err,
          "Couldn't finish setting up your workspace. Try again in a moment.",
        ),
      )
    } finally {
      setProvisioning(false)
    }
  }, [refreshIdentity])

  // Restore the soft-deleted personal org, then let normal identity
  // routing take over (their workspace, or the plan screen if billing
  // holds them). If the hold lapsed between resolve and this click, the
  // server answers 404 (already purged) or 410 (expired, not purged);
  // either way the org is gone, so we re-run resolve, which provisions a
  // fresh org rather than showing a Restore button that can't work.
  const handleRestore = useCallback(async () => {
    if (!recover) return
    setRestoring(true)
    setProvisionError(null)
    try {
      await recoveryAPI.recoverOrg(recover.orgId)
      await refreshIdentity()
    } catch (err) {
      if (err instanceof RecoverError && (err.status === 404 || err.status === 410)) {
        // The org lapsed between resolve and this click — 410 expired (not
        // yet purged) or 404 already purged. Keep the panel and its Log out;
        // offer to start fresh instead of a Restore that can't work.
        setRestoreGone(true)
        setProvisionError(
          err.status === 410
            ? "That organization's recovery window has passed, so it's gone. You can start fresh instead."
            : 'That organization is no longer available. You can start fresh instead.',
        )
      } else {
        setProvisionError(
          toUserMessage(err, "Couldn't restore your organization. Try again in a moment."),
        )
      }
    } finally {
      setRestoring(false)
    }
  }, [recover, refreshIdentity])

  // Start fresh after a lapsed Restore: provision a new personal org. On
  // success it refreshes identity and routes onward; on failure the panel
  // (with Log out) stays, so there is always a way out.
  const handleStartFresh = useCallback(async () => {
    setRestoring(true)
    setProvisionError(null)
    try {
      const result = await onboardingAPI.resolve()
      if (result.action === 'workspace') {
        await refreshIdentity()
        return
      }
      // Any other action here is unexpected (the org is gone) — surface it.
      setProvisionError("Couldn't set up a new workspace. Try again in a moment.")
    } catch (err) {
      setProvisionError(
        toUserMessage(err, "Couldn't set up a new workspace. Try again in a moment."),
      )
    } finally {
      setRestoring(false)
    }
  }, [refreshIdentity])

  const handleLogout = useCallback(async () => {
    await authClient.signOut()
    navigate('/auth/login', { replace: true })
  }, [navigate])

  // Auto-provision once we've confirmed the user has no pending
  // invitations (invite-wins: an invited user accepts above instead).
  // `attemptedRef` keeps this single-shot even if the effect re-runs.
  useEffect(() => {
    if (!isReady || !needsOnboarding) return
    if (invitations === null || invitations.length > 0) return
    if (attemptedRef.current) return
    attemptedRef.current = true
    void runResolve()
  }, [isReady, needsOnboarding, invitations, runResolve])

  async function handleAcceptInvitation(invite: InvitationPreview) {
    setError(null)
    setAcceptingId(invite.id)
    try {
      await invitationsAPI.accept(invite.id)
      // Refresh identity so the now-joined org appears and
      // needsOnboarding flips to false. The /me effect above will
      // then bounce to the workspace runner if the org has one.
      await refreshIdentity()
      // Best-effort direct navigation in case the bounce effect
      // races. Falls through silently if no workspace yet.
      try {
        const ws = await workspacesAPI.getMe()
        navigate(`/workspaces/${ws._id}/results`, { replace: true })
      } catch {
        navigate('/account', { replace: true })
      }
    } catch (err) {
      setAcceptingId(null)
      setError(
        toUserMessage(
          err,
          "Couldn't accept the invitation. Try again — if the link is older than 7 days it may have expired.",
        ),
      )
    }
  }

  // Unauthenticated users get bounced to login by App.tsx's gate, but
  // double-cover here so direct nav doesn't show a half-rendered page.
  if (state.status === 'unauthenticated') {
    return null
  }

  const hasInvitations = (invitations?.length ?? 0) > 0
  const recoverDaysLeft = recover
    ? Math.max(0, Math.ceil((recover.recoverableUntil - Date.now()) / (24 * 60 * 60 * 1000)))
    : 0

  // #2454 — somebody who HAS an organization and no working plan. Distinct
  // from everything below it: they are not waiting on provisioning and there
  // is nothing to retry here, so the provisioning panel would be wrong on
  // every count. Rendered before the invitation branch because an invitation
  // does not give them a plan either.
  //
  // Absent on an install that does not bill its customers, and on a server
  // build from before this, so the screen behaves exactly as it did.
  const gate = state.status === 'ready' ? state.identity.billing : undefined
  const handoffUrl = state.status === 'ready' ? state.identity.billingHandoffUrl : null
  if (gate && gate !== 'fine') {
    return <BillingGate screen={gate} handoffUrl={handoffUrl} />
  }

  return (
    <AuthLayout
      title={
        hasInvitations
          ? "You've been invited"
          : recover
            ? 'Restore your organization?'
            : 'Welcome to OttoTester'
      }
      subtitle={
        hasInvitations
          ? 'Accept an invitation below to join an existing organization.'
          : recover
            ? 'Your organization was recently deleted. Restore it to pick up where you left off, or log out.'
            : "Your account is set up — we're getting your workspace ready."
      }
    >
      {invitations === null && (
        <div className="flex justify-center py-2 mb-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      )}

      {hasInvitations && (
        <div className="mb-6 space-y-3">
          {invitations!.map((inv) => (
            <div
              key={inv.id}
              className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-3"
            >
              <Mail className="h-4 w-4 text-primary mt-0.5" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {inv.organizationName ?? 'Organization'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Invited as {inv.role}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => handleAcceptInvitation(inv)}
                disabled={acceptingId !== null}
              >
                {acceptingId === inv.id ? 'Accepting…' : 'Accept'}
              </Button>
            </div>
          ))}
        </div>
      )}

      {invitations !== null && !hasInvitations && (
        <div className="rounded-md border border-border bg-muted/30 p-4">
          {recover ? (
            <div className="space-y-3" data-testid="recover-panel">
              {restoreGone ? (
                <p className="text-sm" role="alert">
                  {provisionError}
                </p>
              ) : (
                <p className="text-sm">
                  You have{' '}
                  <strong>
                    {recoverDaysLeft} day{recoverDaysLeft === 1 ? '' : 's'}
                  </strong>{' '}
                  left to restore <strong>{recover.orgName}</strong>. Restoring
                  brings back your workspaces and their data.
                </p>
              )}
              {!restoreGone && provisionError && (
                <p className="text-sm text-destructive" role="alert">
                  {provisionError}
                </p>
              )}
              <div className="space-y-2">
                {restoreGone ? (
                  <Button
                    className="w-full"
                    onClick={() => void handleStartFresh()}
                    disabled={restoring}
                  >
                    {restoring ? 'Setting up…' : 'Create a new workspace'}
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    onClick={() => void handleRestore()}
                    disabled={restoring}
                  >
                    {restoring ? 'Restoring…' : `Restore ${recover.orgName}`}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => void handleLogout()}
                  disabled={restoring}
                >
                  Log out
                </Button>
              </div>
            </div>
          ) : provisionError ? (
            <>
              <p className="text-sm" data-testid="provision-error-body">
                {provisionError}
              </p>
              <Button
                size="sm"
                className="mt-3"
                onClick={() => void runResolve()}
                disabled={provisioning}
              >
                {provisioning ? 'Setting up…' : 'Try again'}
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Loader2
                className="h-4 w-4 animate-spin text-muted-foreground"
                aria-hidden="true"
              />
              <p className="text-sm" data-testid="provisioning-body">
                Setting up your workspace…
              </p>
            </div>
          )}
        </div>
      )}

      {/* Single error region — used by both the accept-invitation
          failure path (hasInvitations branch) and the pending-
          provisioning branch. Rendering it outside both conditional
          blocks keeps a single `role="alert"` node regardless of
          which surface the user is on. */}
      {error && (
        <p className="text-sm text-destructive mt-4" role="alert">
          {error}
        </p>
      )}
    </AuthLayout>
  )
}
